import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects';
import arrayMove from 'array-move';
import { LEVEL_SYSTEM_FIELD_MAPPING } from '../../common/constants/SystemFieldConstants';
import envConfig from '../../config';

import {
  fetchFieldDefinitions,
  addSelectedColumn,
  removeSelectedColumn,
  removeAllSelectedColumn,
  reorderColumns,
  saveSelectedColumns,
  fetchGlobalPreSetData,
} from '../routines';
import { request } from '../../common/util/RestClient';
import { LEVEL_SPECIAL_FIELD_MAPPING } from '../../common/constants/SpecialFieldConstants';
import { sortAttributes } from '../../common/util/commonUtil';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import {
  GLOBAL_CONFIG_NAMESAPCE,
  GLOBAL_CONFIG_TYPES,
} from '../../common/constants/globalConfigConstants';
import constants from '../../common/constants/CommonConstants';

const {
  ORGANIZATION_LEVEL,
  ACCOUNT_LEVEL,
  CONTACT_LEVEL,
  PIPELINE_LEVEL,
  RISK_LEVEL,
  OPPORTUNITY_LEVEL,
  CUSTOM_OBJECT_LEVEL,
} = constants;

export function* fetchFieldDefinitionsSaga({ payload }) {
  const { level, scope } = payload;
  try {
    const globalPresetURl = `${envConfig.APIG_URL}core/configs/GLOBAL_PRESET`;
    const { data: globalConfigResult } = yield call(request, globalPresetURl);
    const searchKey = `${
      GLOBAL_CONFIG_NAMESAPCE.LIST_VIEW
    }_${level.toUpperCase()}_ENTITY`;
    const searchResult = globalConfigResult.find(
      (item) => item.key === searchKey
    );

    const URL = `${envConfig.APIG_URL}core/fields?format=object&level=${level}`;
    const fieldDefRs = yield call(request, URL);
    const allDefinitions = {
      ...Object.fromEntries(Object.entries(fieldDefRs.data)),
    };
    const definitions = {
      ...Object.fromEntries(
        Object.entries(fieldDefRs.data).filter(([, value]) => value?.isVisible)
      ),
    };

    const systemLevel = [
      ORGANIZATION_LEVEL,
      ACCOUNT_LEVEL,
      CONTACT_LEVEL,
      PIPELINE_LEVEL,
      OPPORTUNITY_LEVEL,
      RISK_LEVEL,
    ].includes(level)
      ? level
      : CUSTOM_OBJECT_LEVEL;
    const _definitions = {
      ...LEVEL_SYSTEM_FIELD_MAPPING[systemLevel],
      ...LEVEL_SPECIAL_FIELD_MAPPING[level],
      ...definitions,
    };

    const columnConfigURL = `${envConfig.APIG_URL}core/table-column-config?scope=${scope}`;
    const columnConfigRs = yield call(request, columnConfigURL);
    const columnConfig = columnConfigRs.data.filter((field) =>
      Object.values(_definitions).some((def) => def.name === field)
    );

    const shoulOverrideConfig =
      searchResult &&
      parseInt(searchResult.metaData, 10) === GLOBAL_CONFIG_TYPES.ALL_USERS;

    const selected =
      columnConfig.length > 0 || shoulOverrideConfig
        ? columnConfig
        : Object.keys({
            ...LEVEL_SYSTEM_FIELD_MAPPING[systemLevel],
            ...LEVEL_SPECIAL_FIELD_MAPPING[level],
          });

    const unselected = Object.keys(_definitions).filter(
      (field) => selected.indexOf(field) === -1
    );

    yield put(
      fetchFieldDefinitions.success({
        scope,
        selected,
        unselected,
        definitions,
        allDefinitions,
      })
    );
  } catch (error) {
    yield put(fetchFieldDefinitions.failure({ error }));
  }
}

export function* saveSelectedFieldsSaga({ payload }) {
  const { scope, globalPresetType, values } = payload;
  try {
    const field = yield select((state) => state.tableColumns[scope]);
    let URL = `${envConfig.APIG_URL}core/table-column-config`;
    if (globalPresetType) {
      URL += `?globalPresetType=${globalPresetType}`;
    }
    const body = {
      scope,
      config: globalPresetType ? values : field.selected,
    };
    yield call(request, URL, {
      method: 'PUT',
      payload: body,
    });

    if (globalPresetType) {
      yield put(
        saveSelectedColumns.success({
          successMessage: SUCCESS_MESSAGES.GLOBAL_PRESET,
        })
      );
      yield put(fetchGlobalPreSetData.trigger());
    }
  } catch (error) {
    console.error({ error });
  }
}

export function* addSelectedFieldSaga({ payload }) {
  const { value, scope } = payload;
  try {
    const field = yield select((state) => state.tableColumns[scope]);
    const selected = [...field.selected, value];
    const unselected = sortAttributes(field.unselected).filter(
      (unselectedField) => unselectedField !== value
    );
    yield put(addSelectedColumn.success({ scope, selected, unselected }));
  } catch (error) {
    yield put(addSelectedColumn.failure({ error, scope }));
  }
}

export function* removeSelectedFieldSaga({ payload }) {
  const { value, scope } = payload;
  try {
    const field = yield select((state) => state.tableColumns[scope]);
    const selected = field.selected.filter((_value) => _value !== value);
    const unselected = sortAttributes([...field.unselected, value]).sort(
      (a, b) => a.localeCompare(b)
    );
    yield put(removeSelectedColumn.success({ scope, selected, unselected }));
  } catch (error) {
    yield put(removeSelectedColumn.failure({ error, scope }));
  }
}

export function* removeAllSelectedFieldsSaga({ payload }) {
  const { scope } = payload;
  try {
    const field = yield select((state) => state.tableColumns[scope]);
    const unselected = sortAttributes([
      ...field.unselected,
      ...field.selected,
    ]).sort((a, b) => a.localeCompare(b));
    const selected = [];
    yield put(removeAllSelectedColumn.success({ scope, selected, unselected }));
  } catch (error) {
    yield put(removeAllSelectedColumn.failure({ error, scope }));
  }
}

export function* reorderFieldsSaga({ payload }) {
  const { scope } = payload;
  try {
    const field = yield select((state) => state.tableColumns[scope]);
    const selected = [...field.selected];
    const { oldIndex, newIndex } = payload;
    yield put(
      reorderColumns.success({
        scope,
        selected: arrayMove(selected, oldIndex, newIndex),
      })
    );
  } catch (error) {
    yield put(reorderColumns.failure({ error, scope }));
  }
}

export default function* root() {
  yield all([
    takeEvery(fetchFieldDefinitions.TRIGGER, fetchFieldDefinitionsSaga),
    takeLatest(addSelectedColumn.TRIGGER, addSelectedFieldSaga),
    takeLatest(removeSelectedColumn.TRIGGER, removeSelectedFieldSaga),
    takeLatest(removeAllSelectedColumn.TRIGGER, removeAllSelectedFieldsSaga),
    takeLatest(reorderColumns.TRIGGER, reorderFieldsSaga),
    takeLatest(saveSelectedColumns.TRIGGER, saveSelectedFieldsSaga),
  ]);
}
